import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { omit } from 'lodash';
import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { shoppingBagErrorCodes } from '@/errorCodes/orderApp';
import { getCheckoutLineUnitPriceWithoutDiscount } from '@/utils/checkout';

import { Checkout } from '@/components/Checkout';
import { Container } from '@/components/Container';
import { Loader } from '@/components/Loader';
import { ShoppingBag } from '@/components/ShoppingBag';
import { TransactionSummary } from '@/components/TransactionSummary/TransactionSummary';
import { FixedPageDocument } from '@/graphql/contentful/api';
import {
  type CheckoutLine,
  type CheckoutLineFragment,
  type MetadataItem,
  type ProductVariant,
  useShoppingBagProductsQuery,
} from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsEngravingActivated } from '@/hooks/useIsEngravingActivated';
import { useProcessingLine } from '@/hooks/useProcessingLine';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import type { ContentfulFixedPageCMSData } from '@/integrations/contentful';
import {
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { getDropdownValue, getMultiselectValue } from '@/lib/attributes';
import { getLinesIdWithNotSelectedBraceletSize } from '@/lib/checkout';
import {
  CARDS_IMAGE_SIZE,
  GLOBAL_E_MARKET_AVAILABLE,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from '@/lib/const';
import { getEngravingDetailsFromMetadata } from '@/lib/engraving';
import { getErrorMessage, parseErrors } from '@/lib/errors';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { mapEdgesToItems } from '@/lib/maps';
import { getMetaValue } from '@/lib/metadata/helpers';
import {
  excludeProductType,
  isProductPersonalizedEngraving,
} from '@/lib/product';
import { checkIfVatIncluded, getRegionFromLocale } from '@/lib/regions';
import type {
  ApiErrorWithType,
  Maybe,
  ShoppingBagProductWithCheckoutLineId,
} from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';

import { EngravingCard } from '../../components/EngravingCard/EngravingCard';
import { EmptyShoppingBag } from './components/EmptyShoppingBag';
import { RestoreCheckoutInitializer } from './components/RestoreCheckoutInitializer/RestoreCheckoutInitializer';
import { ShoppingBagCard } from './components/ShoppingBagCard';
import {
  getRelatedBuckle,
  getRelatedProductWithCheckoutLineId,
  hasMetadataKey,
} from './components/ShoppingBagCard/utils';
import { ShoppingBagHeaderDesktop } from './components/ShoppingBagHeaderDesktop';
import { ShoppingBagHeaderMobile } from './components/ShoppingBagHeaderMobile';
import {
  getShoppingBagExpressCheckoutKey,
  useShoppingBagExpressMount,
} from './hooks/useShoppingBagExpressMount';

/**
 * Dynamically load ShoppingBagStrapDrawer to reduce initial bundle size
 * This component is only needed when user clicks "Add Strap" button
 */
const ShoppingBagStrapDrawer = dynamic(
  () =>
    import('./components/ShoppingBagStrapDrawer').then(
      (mod) => mod.ShoppingBagStrapDrawer,
    ),
  { ssr: false, loading: () => <Loader height={200} /> },
);

interface ShoppingBagViewProps {
  cms?: ContentfulFixedPageCMSData;
}

export const ShoppingBagView = ({ cms }: ShoppingBagViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const { formatMessage } = useIntl();
  const isEngravingActivated = useIsEngravingActivated();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const [viewCartSent, setViewCartSent] = useState(false);
  const [checkoutValidationErrors, setCheckoutValidationErrors] = useState<
    ApiErrorWithType[]
  >([]);
  const {
    snapshot: {
      isAuthenticated,
      isAuthenticating,
      isInitializing: isAuthInitializing,
      isProcessing: isAuthProcessing,
    },
  } = useUser();
  const isAuthLoading =
    isAuthenticating || isAuthInitializing || isAuthProcessing;
  const {
    lineAdd,
    lineDelete,
    restore,
    snapshot: {
      checkout,
      errors: checkoutErrors,
      isAddPending,
      isInitializing,
      isProcessing,
      preOrderData,
      voucherType,
    },
  } = useCheckout();
  const { sendEcommerceEventByLines } = useGoogleAnalytics();

  const { query } = useRegionProvider();
  const { isBeingProcessed, processLine } = useProcessingLine();
  const [isRestoreInitialized, setIsRestoreInitialized] = useState(false);
  const [braceletSizeErrorLinesId, setBraceletSizeErrorLinesId] = useState<
    string[]
  >([]);
  const { locale, query: queryParams } = useRouter();
  const abandonedCartCheckoutId = queryParams?.checkout;
  const {
    country: { code },
  } = getRegionFromLocale(locale);
  const checkoutLines =
    checkout?.lines?.map((line) => ({
      sku: line.variant.sku ?? '',
      checkoutLineId: line.id,
    })) || [];
  const expressCheckoutKey = getShoppingBagExpressCheckoutKey(
    checkout,
    code,
    locale,
  );
  const isLineProcessing =
    checkout?.lines?.some((line) => isBeingProcessed(line.id)) ?? false;
  const isCheckoutActionBlocked =
    !isRestoreInitialized ||
    isInitializing ||
    isProcessing ||
    !!isAddPending ||
    isLineProcessing;
  const isExpressReadyToMount =
    expressCheckoutKey !== null && !isCheckoutActionBlocked;
  const mountExpressButtons = useShoppingBagExpressMount(
    expressCheckoutKey,
    isExpressReadyToMount,
  );
  const showExpressLoadingPlaceholder =
    expressCheckoutKey !== null && !mountExpressButtons;
  const checkoutVariantSkus = checkoutLines.map(({ sku }) => sku);
  const { data, loading } = useShoppingBagProductsQuery({
    variables: {
      filter: {
        sku: checkoutVariantSkus,
      },
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      thumbnailSize: CARDS_IMAGE_SIZE,
      ...query,
    },
    skip: !checkoutVariantSkus.length,
  });

  const mappedProductVariants = mapEdgesToItems(data?.productVariants);
  const productVariants = checkoutLines
    .map((line) => {
      return {
        ...mappedProductVariants.find((product) => product.sku === line.sku),
        checkoutLineId: line.checkoutLineId,
      };
    })
    .filter(
      (
        productVariant,
      ): productVariant is ShoppingBagProductWithCheckoutLineId =>
        productVariant !== undefined,
    );

  const [currentWatch, setCurrentWatch] =
    useState<ShoppingBagProductWithCheckoutLineId | null>(null);
  const compatibleStrapIds = getMultiselectValue(
    currentWatch?.product.compatibleStraps,
  );

  const areBraceletSizeErrorsSet = () => {
    const linesIdsWithNotSelectedBraceletSize =
      getLinesIdWithNotSelectedBraceletSize(checkout?.lines);

    if (linesIdsWithNotSelectedBraceletSize?.length) {
      // Update state only when the list of error line IDs actually changes
      setBraceletSizeErrorLinesId((prev) => {
        const isSameLength =
          prev.length === linesIdsWithNotSelectedBraceletSize.length;
        const isSameContent =
          isSameLength &&
          prev.every(
            (id, idx) => id === linesIdsWithNotSelectedBraceletSize[idx],
          );

        return isSameContent ? prev : linesIdsWithNotSelectedBraceletSize;
      });
      return false;
    }

    // Clear errors only if some were previously set
    setBraceletSizeErrorLinesId((prev) => (prev.length ? [] : prev));

    return true;
  };

  const errorHandler = genericErrorHandler({
    formatMessage,
    apiErrorsHandler: (errors, errorType) =>
      setErrors(
        errors.map((error) => ({
          type: errorType,
          ...error,
        })),
      ),
    errorType: 'checkout',
  });

  useEffect(() => {
    (async () => {
      if (typeof abandonedCartCheckoutId === 'string') {
        setIsRestoreInitialized(false);
        const result = await restore(abandonedCartCheckoutId);
        if (result?.isExpired) {
          errorHandler([{ code: 'EXPIRED' }]);
        }
      }
      setIsRestoreInitialized(true);
    })();
  }, [abandonedCartCheckoutId]);

  useEffect(() => {
    const isVatIncluded = checkIfVatIncluded(checkout?.channel?.slug);
    setBraceletSizeErrorLinesId([]);

    if (checkout?.lines && !viewCartSent) {
      sendEcommerceEventByLines({
        coupon: checkout.voucherCode ?? undefined,
        event: EVENT_NAME.VIEW_CART,
        lines: checkout.lines,
        totalValue: isVatIncluded
          ? checkout?.totalPrice?.gross?.amount
          : checkout?.totalPrice?.net?.amount,
      });
      setViewCartSent(true);
    }
  }, [checkout?.lines]);

  const handleDelete = useCallback(
    (lineId: string) => {
      // Deleting the line should automatically delete the related straps (and buckle), warranty and engraving
      processLine(lineId, lineDelete);
    },
    [processLine, lineDelete],
  );

  if (!isRestoreInitialized)
    return <Loader data-testid='ShoppingBagViewLoader' height={400} />;

  if (!checkout?.lines?.length) {
    return (
      <Checkout data-test-id='ShoppingBagViewCheckout'>
        {liveCms?.topContent?.map((data, i) => (
          <Fragment key={generateSectionKey(data, i)}>
            {isAuthLoading
              ? null
              : renderContentfulFixedPage(
                  data,
                  undefined,
                  liveCms?.cmsExtraData,
                  false,
                  isAuthenticated,
                )}
          </Fragment>
        ))}
        <EmptyShoppingBag
          data-test-id='ShoppingBagViewEmptyShoppingBag'
          errors={errors}
          isInitializing={isInitializing}
        />
        {liveCms?.bottomContent?.map((data, i) => (
          <Fragment key={generateSectionKey(data, i)}>
            {isAuthLoading
              ? null
              : renderContentfulFixedPage(
                  data,
                  undefined,
                  liveCms?.cmsExtraData,
                  false,
                  isAuthenticated,
                )}
          </Fragment>
        ))}
      </Checkout>
    );
  }

  const shoppingBagErrors = parseErrors(
    [
      ...(checkoutErrors?.filter((item) =>
        shoppingBagErrorCodes?.includes(item.code),
      ) || []),
      ...(errors?.filter((item) =>
        shoppingBagErrorCodes?.includes(item.code),
      ) || []),
      ...(checkoutValidationErrors?.filter((item) =>
        shoppingBagErrorCodes?.includes(item.code),
      ) || []),
    ],
    formatMessage,
  );
  const nonShoppingBagErrors = [
    ...errors.filter((item) => !shoppingBagErrorCodes?.includes(item.code)),
    ...checkoutValidationErrors.filter(
      (item) => !shoppingBagErrorCodes?.includes(item.code),
    ),
  ];

  const showExpressButton = !GLOBAL_E_MARKET_AVAILABLE?.includes(locale);

  return (
    <Checkout data-test-id='ShoppingBagViewCheckout'>
      <Container>
        <ShoppingBagHeaderDesktop
          data-test-id='ShoppingBagViewShoppingBagHeaderDesktop'
          showBackLink
        />
        {liveCms?.topContent?.map((data, i) => (
          <Fragment key={generateSectionKey(data, i)}>
            {isAuthLoading
              ? null
              : renderContentfulFixedPage(
                  data,
                  undefined,
                  liveCms?.cmsExtraData,
                  true,
                  isAuthenticated,
                )}
          </Fragment>
        ))}
      </Container>
      <Checkout.Content>
        <ShoppingBagHeaderMobile data-test-id='ShoppingBagViewShoppingBagHeaderMobile' />
        {isInitializing ? (
          <Loader data-test-id='ShoppingBagViewLoader' />
        ) : (
          <>
            <Checkout.Section withBackground={!loading}>
              {loading ? (
                <Loader data-test-id='ShoppingBagViewLoader' />
              ) : (
                <>
                  <Checkout.ServerErrors
                    data-test-id='PaymentViewServerErrors'
                    errors={nonShoppingBagErrors}
                  />
                  {productVariants
                    .filter((productVariant) => {
                      const { checkoutLineId } = productVariant;
                      const line = checkout.lines?.find(
                        (line) => line.id === checkoutLineId,
                      );
                      return (
                        !line?.relatedProductWatchId &&
                        !line?.strapPackReference
                      );
                    })
                    .filter((productVariant) =>
                      excludeProductType(
                        productVariant.product?.productType,
                        'buckle',
                      ),
                    )
                    .filter((productVariant) =>
                      excludeProductType(
                        productVariant.product?.productType,
                        'colorado-tax-dummy',
                      ),
                    )
                    .map((productVariant, index) => {
                      const { checkoutLineId, product, sku } = productVariant;
                      const errorMessage = productVariant
                        ? getErrorMessage(
                            omit(
                              productVariant,
                              checkoutLineId,
                            ) as ProductVariant,
                            shoppingBagErrors,
                          )
                        : '';

                      const line = checkout.lines?.find(
                        (line) => line.id === checkoutLineId,
                      );

                      if (!line || !productVariant.product) {
                        return null;
                      }

                      const relatedStraps = checkout.lines?.filter(
                        (checkoutLine) => {
                          if (product.productType.slug === 'strap-pack') {
                            return (
                              !['engraving', 'warranty', 'buckle'].includes(
                                checkoutLine.variant.product.productType.slug,
                              ) && checkoutLine.strapPackReference === sku
                            );
                          }
                          return (
                            !['engraving', 'warranty'].includes(
                              checkoutLine.variant.product.productType.slug,
                            ) &&
                            checkoutLine.relatedProductWatchId ===
                              checkoutLineId
                          );
                        },
                      );

                      const relatedBuckle: Maybe<CheckoutLineFragment> =
                        line && getRelatedBuckle(checkout.lines, line);

                      const showBuckleSizeSelector =
                        productVariant.product?.strapColor?.values[0]?.name ===
                        'Metal bracelet';

                      const hasBraceletSizeNotSelectedError =
                        !!braceletSizeErrorLinesId.find(
                          (id) => id === line?.id,
                        );

                      const hasRelatedStrap = !!relatedStraps?.length;
                      const hasRelatedCard =
                        hasRelatedStrap &&
                        (line.variant.product.productType.name.toLowerCase() ===
                          'watch' ||
                          line.variant.product.productType.name.toLowerCase() ===
                            'strap pack');

                      const hasRelatedExtendWarranty = hasMetadataKey(
                        line as CheckoutLine,
                        'has.warranty_ext',
                      );
                      const relatedWarrantyWithCheckoutLineId =
                        getRelatedProductWithCheckoutLineId(
                          checkout.lines,
                          line?.id,
                          'watch',
                          'warranty',
                        );
                      const hasExtendWarranty =
                        hasRelatedExtendWarranty &&
                        relatedWarrantyWithCheckoutLineId;

                      const hasRelatedEngraving = hasMetadataKey(
                        line as CheckoutLine,
                        'related.engraving',
                      );
                      const relatedEngravingWithCheckoutLineId =
                        getRelatedProductWithCheckoutLineId(
                          checkout.lines,
                          line?.id,
                          'watch',
                          'engraving',
                        );
                      const hasEngraving =
                        hasRelatedEngraving &&
                        relatedEngravingWithCheckoutLineId;

                      const warrantyExtensionDuration =
                        getDropdownValue(
                          productVariant.product.warrantyExtensionDuration,
                        ) || getMetaValue(line.metadata, 'warranty.duration');

                      const lineUnitPriceWithDiscount =
                        getCheckoutLineUnitPriceWithoutDiscount(
                          line,
                          voucherType,
                        );

                      const isPersonalizedEngravingProduct =
                        isProductPersonalizedEngraving(productVariant.product);

                      const engravingDetails = getEngravingDetailsFromMetadata(
                        relatedEngravingWithCheckoutLineId?.metadata as readonly MetadataItem[],
                      );
                      const isTopLevelLcp = index === 0;

                      return (
                        <Fragment key={checkoutLineId}>
                          <ShoppingBagCard
                            channel={checkout?.channel?.slug}
                            customErrorMessage={errorMessage}
                            data-test-id={`ShoppingBagViewShoppingBagCard-${checkoutLineId}`}
                            hasBraceletSizeNotSelectedError={
                              hasBraceletSizeNotSelectedError
                            }
                            hasEngraving={!!hasEngraving}
                            hasExtendWarranty={!!hasRelatedExtendWarranty}
                            hasRelatedStrap={hasRelatedStrap}
                            isDisabled={isProcessing}
                            isEagerLoading={isTopLevelLcp}
                            isProcessing={isProcessing}
                            lineId={line.id}
                            onAddStrap={setCurrentWatch}
                            onDelete={(lineId) => handleDelete(lineId)}
                            onLineAddItem={lineAdd}
                            preOrderData={preOrderData}
                            price={lineUnitPriceWithDiscount}
                            productVariant={
                              productVariant as ShoppingBagProductWithCheckoutLineId
                            }
                            relatedBuckle={relatedBuckle}
                            showBuckleSizeSelector={showBuckleSizeSelector}
                            voucherType={voucherType}
                            warrantyExtensionDuration={
                              warrantyExtensionDuration
                            }
                          >
                            {hasRelatedCard &&
                              relatedStraps?.map((relatedStrap) => {
                                const { id: relatedStrapCheckoutId } =
                                  relatedStrap;
                                const Strapline = checkout.lines?.find(
                                  (line) => line.id === relatedStrapCheckoutId,
                                );
                                const relatedBuckleToRelatedStrap: Maybe<CheckoutLineFragment> =
                                  Strapline &&
                                  getRelatedBuckle(checkout.lines, Strapline);

                                const relatedStrapUnitPriceWithDiscount =
                                  getCheckoutLineUnitPriceWithoutDiscount(
                                    relatedStrap,
                                    voucherType,
                                  );

                                return (
                                  relatedStrap && (
                                    <ShoppingBagCard
                                      key={relatedStrap?.id}
                                      channel={checkout?.channel?.slug}
                                      data-test-id={`ShoppingBagViewShoppingBagCard-${relatedStrap?.id}`}
                                      hasBraceletSizeNotSelectedError={
                                        hasBraceletSizeNotSelectedError
                                      }
                                      hasExtendWarranty={!!hasExtendWarranty}
                                      hasRelatedStrap={true}
                                      isDisabled={isProcessing}
                                      isProcessing={
                                        isBeingProcessed(
                                          relatedStrap?.id ?? '',
                                        ) ||
                                        isBeingProcessed(
                                          relatedBuckleToRelatedStrap?.id || '',
                                        )
                                      }
                                      isRelatedProduct
                                      lineId={line.id}
                                      onDelete={(lineId) => {
                                        processLine(lineId, lineDelete);
                                      }}
                                      price={relatedStrapUnitPriceWithDiscount}
                                      productVariant={{
                                        ...relatedStrap?.variant,
                                        checkoutLineId: relatedStrap?.id,
                                      }}
                                      relatedBuckle={
                                        relatedBuckleToRelatedStrap
                                      }
                                      showBuckleSizeSelector={false}
                                      strapPack={
                                        product.productType.slug ===
                                          'strap-pack' &&
                                        Strapline?.variant.product.productType
                                          .slug !== 'tool'
                                      }
                                      voucherType={voucherType}
                                    />
                                  )
                                );
                              })}
                            {isEngravingActivated &&
                              isPersonalizedEngravingProduct &&
                              hasEngraving &&
                              engravingDetails && (
                                <EngravingCard
                                  key={checkoutLineId}
                                  data-test-id={`ShoppingBagViewEngravingCard-${checkoutLineId}`}
                                  engravingDetails={engravingDetails}
                                  hasBraceletSizeNotSelectedError={
                                    hasBraceletSizeNotSelectedError
                                  }
                                  isDisabled={isProcessing}
                                  isProcessing={isProcessing}
                                  isRelatedProduct
                                  lineId={line.id}
                                  onDelete={() => {
                                    processLine(
                                      relatedEngravingWithCheckoutLineId.checkoutLineId,
                                      lineDelete,
                                    );
                                  }}
                                  productVariant={{
                                    ...relatedEngravingWithCheckoutLineId.variant,
                                    checkoutLineId: checkoutLineId,
                                  }}
                                />
                              )}
                            {hasExtendWarranty && (
                              <ShoppingBagCard
                                key={
                                  relatedWarrantyWithCheckoutLineId.checkoutLineId
                                }
                                channel={checkout?.channel?.slug}
                                data-test-id={`ShoppingBagViewShoppingBagCard-${relatedWarrantyWithCheckoutLineId.checkoutLineId}`}
                                hasBraceletSizeNotSelectedError={
                                  hasBraceletSizeNotSelectedError
                                }
                                hasExtendWarranty={true}
                                hasRelatedStrap={hasRelatedStrap}
                                isDisabled={isProcessing}
                                isProcessing={isBeingProcessed(
                                  relatedWarrantyWithCheckoutLineId.checkoutLineId,
                                )}
                                isRelatedProduct
                                lineId={line.id}
                                onDelete={(lineId) => {
                                  processLine(lineId, lineDelete);
                                }}
                                price={
                                  relatedWarrantyWithCheckoutLineId.unitPrice
                                }
                                productVariant={{
                                  ...relatedWarrantyWithCheckoutLineId.variant,
                                  checkoutLineId:
                                    relatedWarrantyWithCheckoutLineId.checkoutLineId,
                                }}
                                relatedBuckle={null}
                                showBuckleSizeSelector={false}
                                voucherType={voucherType}
                                warrantyExtensionDuration={
                                  warrantyExtensionDuration
                                }
                              />
                            )}
                          </ShoppingBagCard>
                        </Fragment>
                      );
                    })}
                </>
              )}
              {currentWatch && (
                <ShoppingBagStrapDrawer
                  channel={checkout?.channel?.slug}
                  compatibleStrapIds={compatibleStrapIds}
                  data-test-id='ShoppingBagViewStrapDrawer'
                  onClose={() => setCurrentWatch(null)}
                  orderCtaType={preOrderData ? 'PRE_ORDER' : 'ORDER'}
                  watch={currentWatch}
                />
              )}
            </Checkout.Section>
            <Checkout.Section>
              <ShoppingBag data-test-id='ShoppingBagViewShoppingBag'>
                <ShoppingBag.PrivilegeOffer
                  data-test-id='ShoppingBagViewShoppingBagPrivilegeOffer'
                  isCheckoutStepTwo={false}
                />
                <TransactionSummary
                  areBraceletSizeErrorsSet={areBraceletSizeErrorsSet}
                  atomicExpressReveal
                  channel={checkout?.channel?.slug}
                  code={code}
                  errorHandler={errorHandler}
                  expressLazyMount={false}
                  fragment={checkout}
                  isExpressCheckoutBlocked={isCheckoutActionBlocked}
                  isPricePending={isCheckoutActionBlocked}
                  isProcessing={isCheckoutActionBlocked}
                  mountExpressButtons={mountExpressButtons}
                  preOrderData={preOrderData}
                  salesTax={checkout?.totalPrice?.tax}
                  setCheckoutValidationErrors={setCheckoutValidationErrors}
                  showButton={true}
                  showExpressButton={showExpressButton}
                  showExpressLoadingPlaceholder={showExpressLoadingPlaceholder}
                  showGiftBox={GLOBAL_E_MARKET_AVAILABLE?.includes(locale)}
                  showTax={checkIfVatIncluded(checkout?.channel?.slug)}
                  showTaxCalculationInfo={false}
                  voucherType={voucherType}
                />
              </ShoppingBag>
            </Checkout.Section>
          </>
        )}
      </Checkout.Content>
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {isAuthLoading
            ? null
            : renderContentfulFixedPage(
                data,
                undefined,
                liveCms?.cmsExtraData,
                true,
                isAuthenticated,
              )}
        </Fragment>
      ))}
    </Checkout>
  );
};

ShoppingBagView.getLayout = (page: ReactElement) => (
  <Default
    backgroundColor='lightGrey'
    cmsData={page.props.cmsDefaultLayoutData}
  >
    <RestoreCheckoutInitializer>{page}</RestoreCheckoutInitializer>
  </Default>
);
